/**
 * MEVIA V2 - Assessment Method Selector Component
 * 
 * Allows users to choose between:
 * - Token distribution (existing method)
 * - Pairwise comparison (new method)
 * 
 * Respects company configuration for which methods are enabled
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Coins, GitCompare, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const TEXTS = {
  es: {
    title: 'Selecciona el Método de Evaluación',
    subtitle: 'Ambos métodos te darán resultados equivalentes',
    tokenTitle: 'Distribución de Puntos',
    tokenDescription: 'Distribuye 100 puntos entre las 10 dimensiones según tu percepción. Tienes control total sobre cómo asignas tus puntos.',
    tokenTime: '~45 minutos',
    tokenFeatures: ['Control preciso', 'Vista completa', 'Ajustes en tiempo real'],
    pairwiseTitle: 'Comparación por Pares',
    pairwiseDescription: 'Compara declaraciones de dos dimensiones y elige la que más te representa. Más intuitivo y rápido.',
    pairwiseTime: '~30 minutos',
    pairwiseFeatures: ['Más intuitivo', 'Decisiones simples', 'Menos fatiga'],
    recommended: 'Recomendado',
    selectMethod: 'Seleccionar',
    estimatedTime: 'Tiempo estimado',
    selected: 'Seleccionado',
    onlyMethodAvailable: 'Este es el único método disponible configurado por tu empresa'
  },
  en: {
    title: 'Select Assessment Method',
    subtitle: 'Both methods will give you equivalent results',
    tokenTitle: 'Point Distribution',
    tokenDescription: 'Distribute 100 points among the 10 dimensions according to your perception. You have full control over how you allocate your points.',
    tokenTime: '~45 minutes',
    tokenFeatures: ['Precise control', 'Full overview', 'Real-time adjustments'],
    pairwiseTitle: 'Pairwise Comparison',
    pairwiseDescription: 'Compare statements from two dimensions and choose the one that represents you best. More intuitive and faster.',
    pairwiseTime: '~30 minutes',
    pairwiseFeatures: ['More intuitive', 'Simple decisions', 'Less fatigue'],
    recommended: 'Recommended',
    selectMethod: 'Select',
    estimatedTime: 'Estimated time',
    selected: 'Selected',
    onlyMethodAvailable: 'This is the only method available configured by your company'
  }
};

const AssessmentMethodSelector = ({ onSelectMethod, defaultMethod = null }) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const texts = TEXTS[language] || TEXTS.es;
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState(defaultMethod);
  
  // Config state
  const [tokensEnabled, setTokensEnabled] = useState(true);
  const [pairwiseEnabled, setPairwiseEnabled] = useState(true);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      // Load assessment config to check which methods are enabled
      if (user?.empresa_id) {
        const configRes = await axios.get(`/v2/assessment-config/resolve?empresa_id=${user.empresa_id}`);
        if (configRes.data.success) {
          const config = configRes.data.data;
          setTokensEnabled(config.tokens_method_enabled !== false);
          setPairwiseEnabled(config.pairwise_method_enabled !== false);
          
          // If only one method is enabled, auto-select it
          if (config.tokens_method_enabled && !config.pairwise_method_enabled) {
            setSelectedMethod('tokens');
            if (onSelectMethod) onSelectMethod('tokens');
          } else if (!config.tokens_method_enabled && config.pairwise_method_enabled) {
            setSelectedMethod('pairwise');
            if (onSelectMethod) onSelectMethod('pairwise');
          }
        }
      }
      
      // Load methods info
      const response = await axios.get(`/v2/assessment-methods?language=${language}`);
      if (response.data.success) {
        setMethods(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (methodKey) => {
    setSelectedMethod(methodKey);
    if (onSelectMethod) {
      onSelectMethod(methodKey);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Count enabled methods
  const enabledMethodsCount = (tokensEnabled ? 1 : 0) + (pairwiseEnabled ? 1 : 0);
  const showBothMethods = enabledMethodsCount === 2;

  // If only one method is enabled, show a simplified view
  if (enabledMethodsCount === 1) {
    const enabledMethod = tokensEnabled ? 'tokens' : 'pairwise';
    const methodTexts = enabledMethod === 'tokens' 
      ? { title: texts.tokenTitle, desc: texts.tokenDescription, time: texts.tokenTime, features: texts.tokenFeatures, icon: Coins, color: 'blue' }
      : { title: texts.pairwiseTitle, desc: texts.pairwiseDescription, time: texts.pairwiseTime, features: texts.pairwiseFeatures, icon: GitCompare, color: 'green' };
    
    const IconComponent = methodTexts.icon;
    
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">{methodTexts.title}</h2>
          <p className="text-gray-600 mt-2">{texts.onlyMethodAvailable}</p>
        </div>
        
        <Card className={`ring-2 ring-${methodTexts.color}-500 shadow-lg max-w-md mx-auto`}>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className={`w-12 h-12 rounded-xl bg-${methodTexts.color}-100 flex items-center justify-center`}>
                <IconComponent className={`w-6 h-6 text-${methodTexts.color}-600`} />
              </div>
              <CheckCircle className={`w-6 h-6 text-${methodTexts.color}-600`} />
            </div>
            <CardTitle className="text-lg mt-3">{methodTexts.title}</CardTitle>
            <CardDescription className="text-sm">{methodTexts.desc}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4" />
                <span>{texts.estimatedTime}: {methodTexts.time}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {methodTexts.features.map((feature, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {feature}
                  </Badge>
                ))}
              </div>
              <Button 
                className={`w-full mt-4 bg-${methodTexts.color}-600 hover:bg-${methodTexts.color}-700`}
                onClick={() => handleSelect(enabledMethod)}
              >
                <CheckCircle className="w-4 h-4 mr-2" /> {texts.selected}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">{texts.title}</h2>
        <p className="text-gray-600 mt-2">{texts.subtitle}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Token Distribution Method - Only show if enabled */}
        {tokensEnabled && (
          <Card 
            className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
              selectedMethod === 'tokens' 
                ? 'ring-2 ring-blue-500 shadow-lg' 
                : 'hover:ring-1 hover:ring-blue-300'
            }`}
            onClick={() => handleSelect('tokens')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Coins className="w-6 h-6 text-blue-600" />
                </div>
                {selectedMethod === 'tokens' && (
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                )}
              </div>
              <CardTitle className="text-lg mt-3">{texts.tokenTitle}</CardTitle>
              <CardDescription className="text-sm">
                {texts.tokenDescription}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>{texts.estimatedTime}: {texts.tokenTime}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {texts.tokenFeatures.map((feature, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {feature}
                    </Badge>
                  ))}
                </div>
                <Button 
                  className={`w-full mt-4 ${
                    selectedMethod === 'tokens' 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect('tokens');
                  }}
                >
                  {selectedMethod === 'tokens' ? (
                    <><CheckCircle className="w-4 h-4 mr-2" /> {texts.selected}</>
                  ) : (
                    <>{texts.selectMethod} <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pairwise Comparison Method - Only show if enabled */}
        {pairwiseEnabled && (
          <Card 
            className={`cursor-pointer transition-all duration-200 hover:shadow-lg relative ${
              selectedMethod === 'pairwise' 
                ? 'ring-2 ring-green-500 shadow-lg' 
                : 'hover:ring-1 hover:ring-green-300'
            }`}
            onClick={() => handleSelect('pairwise')}
          >
            {showBothMethods && (
              <div className="absolute -top-3 right-4">
                <Badge className="bg-green-600 text-white">
                  {texts.recommended}
                </Badge>
              </div>
            )}
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <GitCompare className="w-6 h-6 text-green-600" />
                </div>
                {selectedMethod === 'pairwise' && (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                )}
              </div>
              <CardTitle className="text-lg mt-3">{texts.pairwiseTitle}</CardTitle>
              <CardDescription className="text-sm">
                {texts.pairwiseDescription}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>{texts.estimatedTime}: {texts.pairwiseTime}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {texts.pairwiseFeatures.map((feature, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs bg-green-50 text-green-700">
                      {feature}
                    </Badge>
                  ))}
                </div>
                <Button 
                  className={`w-full mt-4 ${
                    selectedMethod === 'pairwise' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect('pairwise');
                  }}
                >
                  {selectedMethod === 'pairwise' ? (
                    <><CheckCircle className="w-4 h-4 mr-2" /> {texts.selected}</>
                  ) : (
                    <>{texts.selectMethod} <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AssessmentMethodSelector;
